import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TcApiError } from '@tc-core/model/it/codegen/tbx/ext/errors/tc-api-error';
import { TcHttpError } from '@tc-core/model/it/codegen/tbx/ext/errors/tc-http-error';
import { LEVEL, ModalData } from '@tc-core/model/it/codegen/ui/framework/modal-data';
import { Pager } from '@tc-core/model/it/codegen/ui/framework/pager';
import { Sorter } from '@tc-core/model/it/codegen/ui/framework/sorter';
import { SummaryCardData } from '@tc-core/model/it/codegen/ui/framework/summary-card-data';
import { SorterService } from '@tc-core/service/sorters/sorter.service';
import { ConfigLoader } from '@tc-core/util/framework/config-loader.service';
import { UserJourneyManager } from '@tc-core/util/framework/user-journey-manager.service';
import { CommonHelper } from '@tc-core/util/helpers/common-helper.service';
import { AutoScrollService } from '@tc-core/util/ui/auto-scroll.service';
import { SpinnerService } from '@tc-core/util/ui/spinner.service';
import { FocusViewService } from '@tc/focus-view/focus-view.service';
import { Subscription } from 'rxjs';
import { TCO } from '../../constants';
import { GenericSearchCriteria } from '../../models/criteria/generic-search-criteria';
import { ResourceTypeSearchCriteria } from '../../models/criteria/resource-type-search-criteria';
import { Resource } from '../../models/resource/resource';
import { OPResponseWrapper } from '../../models/response/op-response-wrapper';
import { GenericResourceSummary } from '../../models/summary/generic-resource-summary';
import { GenResourceCost } from '../../models/supplier/gen-resource-cost-setup';
import { Season } from '../../models/supplier/season';
import { Supplier } from '../../models/supplier/supplier';
import { TrsResourceCost } from '../../models/supplier/trs-resource-cost-setup';
import { DataHandlerService } from '../../services/backend-consumers/master-data-handler-service/data-handler.service';
import { ResourceTypeService } from '../../services/backend-consumers/setups/resource-type.service';
import { GenericResourceService } from '../../services/backend-consumers/supplier-service/generic-resource.service';
import { SupplierService } from '../../services/backend-consumers/supplier-service/supplier.service';
import { RootService } from '../../services/util/core-services/root.service';
import { DataKey, DataStoreService } from '../../services/util/framework/data-store.service';
import { DMCQueryParamsService } from '../../services/util/framework/dmc-query-params.service';
import { ResponseUtil } from '../../services/util/response/response-util.service';
import { ContentSummaryCardService } from '../../services/util/ui/content-summary-card.service';
import { ResAvailabilityCalendarFocusViewComponent } from '../../widgets/shared/res-availability-calendar-focus-view/res-availability-calendar-focus-view.component';

@Component({
    selector: 'tc-generic-search-results',
    templateUrl: './generic-search-results.component.html'
})
export class GenericSearchResultsComponent implements OnInit, OnDestroy {

    tGenericResourceGeneric: any;
    tGenericResourceGuide: any;
    tGenericResourceDrive: any;

    configs: any = {};

    heading: string = '';

    public pager: Pager;
    public defaultPageResultCount = 10;
    selectionIndex = '';
    tPagination: any;
    fetchedReservationsCount: number = 0;
    dataFromMoreClick = false;
    paginatorHasMore = true;

    resultCount: number = 0;

    searchCriteria = new GenericSearchCriteria();

    sorters_: Sorter[];

    searchResults: GenericResourceSummary[];
    genericResourceDataArray: any = [];
    contentCardDataList: SummaryCardData[] = [];
    selectedGenericResourceSummary: SummaryCardData = null;
    detailedResource: Resource;

    resourceTypes: any[] = [];
    transferModes: any[] = [];
    private genericTypePathName: any;
    resourceCost: Season[] = [];
    firstTimeSupplierResults: Boolean = false;
    navConfig: any[] = [];

    private focusViewCloseSubject: Subscription = new Subscription();
    private genericSearchResultSubscription: Subscription = new Subscription();
    private genericSearchResultFromServiceSubscription: Subscription = new Subscription();
    private genericSearchResultFromServiceOnMoreSubscription: Subscription = new Subscription();
    private genericSearchCriteriaSubscription: Subscription = new Subscription();
    private supplierSubscription: Subscription = new Subscription();

    constructor(
        private configLoader: ConfigLoader,
        private genericResourceService: GenericResourceService,
        private dataStore: DataStoreService,
        private supplierService: SupplierService,
        public sorterService: SorterService,
        private summaryCardService: ContentSummaryCardService,
        private spinnerService: SpinnerService,
        private userJourneyManager: UserJourneyManager,
        private dataHandlerService: DataHandlerService,
        private resourceTypeService: ResourceTypeService,
        private commonHelper: CommonHelper,
        private activatedRoute: ActivatedRoute,
        private rootService: RootService,
        private focusViewService: FocusViewService,
        private queryParamService: DMCQueryParamsService,
        private autoScrollService: AutoScrollService
    ) { }

    ngOnInit() {
        this.tGenericResourceGeneric = this.configLoader.configurations.get(TCO.CONF.CONF_GENERIC_SEARCH_RESULTS_CONFIG);
        this.tGenericResourceGuide = this.configLoader.configurations.get(TCO.CONF.CONF_GUIDE_SEARCH_RESULTS_CONFIG);
        this.tGenericResourceDrive = this.configLoader.configurations.get(TCO.CONF.CONF_DRIVER_SEARCH_RESULTS_CONFIG);
        this.createConfigMap();

        this.tPagination = this.configLoader.configurations.get(TCO.CONF.CONF_PAGINATION_CONFIG)['GENERIC_RESOURCE_SEARCH'];

        this.setupPaginator();
        this.getSearchCriteria();
        this.subscribeSearchResults();
        this.getSearchResults();
        this.createSorters();

        const resourceTypeSearchCriteria = new ResourceTypeSearchCriteria();
        resourceTypeSearchCriteria.size = 1000000;
        this.resourceTypeService.getResourceTypes(resourceTypeSearchCriteria).subscribe(
            result => {
                if (result.data) {
                    this.resourceTypes = result.data;
                }
            });

        this.dataHandlerService.retrieveTransferModes().subscribe(
            result => {
                if (result.data) {
                    this.transferModes = result.data;
                }
            });
    }

    createConfigMap() {
        this.configs['generic'] = this.tGenericResourceGeneric;
        this.configs['driver'] = this.tGenericResourceDrive;
        this.configs['guide'] = this.tGenericResourceGuide;
    }

    private setupPaginator() {
        this.defaultPageResultCount = this.tPagination['defaultPageResultCount'];
        this.pager = new Pager(0, this.defaultPageResultCount);
    }

    public onPage(pageNumber: number) {
        this.pager.currPage = pageNumber;
        this.selectionIndex = (this.pager.currPage * this.pager.itemsPerPage).toString();
    }

    public onSelection(event) {
        this.selectionIndex = event;
    }

    public onMore() {
        this.dataFromMoreClick = true;
        if (this.tPagination && this.tPagination['defaultPageResultCount'] && this.tPagination['fetchingPages']) {
            this.searchCriteria.size = this.tPagination['fetchingPages'] * this.tPagination['defaultPageResultCount'];
            this.searchCriteria.start = this.fetchedReservationsCount;
            this.retrieveAndCollectSearchResults(this.searchCriteria);
        }
    }

    private setPageZero() {
        if (this.pager) {
            if (!this.dataFromMoreClick) {
                this.pager.currPage = 0;
            } else {
                this.dataFromMoreClick = false;
            }
        } else {
            this.pager = new Pager(0, this.defaultPageResultCount);
        }
    }

    subscribeSearchResults() {
        this.genericSearchResultFromServiceSubscription = this.dataStore.get(DataKey.genericResourceSearchResultFromService)
                                                              .subscribe((result: OPResponseWrapper<GenericResourceSummary>) => {
                                                                  this.resultCount = 0;
                                                                  if (result) {
                                                                      this.resultCount = ResponseUtil.getTotalCount(result);
                                                                      let dataList = ResponseUtil.getDataArray(result);
                                                                      this.dataStore.set(
                                                                          DataKey.genericResourceSearchResult,
                                                                          dataList,
                                                                          true
                                                                      );
                                                                  }
                                                              });
    }

    // -- paginator--

    /**
     * keep fetched data and add new data to dataStore for pagination. only use in pagination more
     *
     * @param searchCriteria
     */
    retrieveAndCollectSearchResults(searchCriteria: GenericSearchCriteria) {
        if (this.genericSearchResultFromServiceOnMoreSubscription) {
            this.genericSearchResultFromServiceOnMoreSubscription.unsubscribe();
        }
        this.genericSearchResultFromServiceOnMoreSubscription = this.genericResourceService.searchGenericResourceForPaging(searchCriteria)
                                                                    .subscribe(result => {
                                                                        if (result) {
                                                                            this.resultCount = ResponseUtil.getTotalCount(result);
                                                                            let dataList = this.searchResults.concat(ResponseUtil.getDataArray(
                                                                                result));
                                                                            this.dataStore.set(
                                                                                DataKey.genericResourceSearchResult,
                                                                                dataList,
                                                                                true
                                                                            );
                                                                        }
                                                                    });
    }

    getSearchCriteria() {
        this.genericSearchCriteriaSubscription = this.dataStore.get(DataKey.genericSearchCriteria)
                                                     .subscribe((data) => {
                                                         if (data !== null) {
                                                             this.searchCriteria = data;
                                                             this.createSorters();
                                                         }
                                                     });
    }

    createSorters() {
        if (this.searchCriteria) {
            this.sorterService.sorters = [];
            this.sorters_ = [];
            let sorterMap = new Map<string, Sorter>();

            let specificConfig = null;

            if (this.searchCriteria.resourceType && this.configs[this.searchCriteria.resourceType]) {
                specificConfig = this.configs[this.searchCriteria.resourceType];
            } else if (this.configs['generic']) {
                specificConfig = this.configs['generic'];
            }
            if (specificConfig.config && specificConfig.config.placeHolderConfig) {
                specificConfig.config.placeHolderConfig.forEach(config => {
                    if (config && config.sort) {
                        let sorter = this.sorterService.createSorter(config);
                        let id = config['_id'];
                        sorterMap.set(id, sorter);
                        this.sorters_.push(sorter);
                    }
                });
            }
        }
    }

    public onSort(event) {
        this.sorterService.sorter = event;
    }

    getSearchResults() {
        this.fetchedReservationsCount = 0;
        this.genericSearchResultSubscription = this.dataStore.get(DataKey.genericResourceSearchResult)
                                                   .subscribe(data => {
                                                       this.selectionIndex = '';

                                                       this.searchResults = [];
                                                       if (data != null) {
                                                           this.spinnerService.hide();
                                                       }
                                                       if (data) {
                                                           this.searchResults = data;
                                                           this.fetchedReservationsCount = this.searchResults.length;
                                                           if (this.fetchedReservationsCount > 0) {
                                                           }
                                                           this.getGenericResources();
                                                       } else {
                                                           this.contentCardDataList = [];
                                                           this.heading = '';
                                                       }
                                                   });
    }

    getGenericResources() {
        if (this.searchResults.length !== null) {
            let genericResources: GenericResourceSummary[] = this.searchResults;

            let genericResourceMap = [];
            for (let i = 0; i < genericResources.length; i++) {

                let id = genericResources[i].resourceId;
                let name = (genericResources[i].firstName ?
                    genericResources[i].firstName + ' ' :
                    '') + (genericResources[i].lastName ? genericResources[i].lastName : '-');
                let grade = genericResources[i].grade;
                let workingHours = genericResources[i].workingHours;
                let supplierId = genericResources[i].supplierId;
                let supplierName = genericResources[i].supplierName;
                let supplierCode = genericResources[i].supplierCode;
                let minPax = genericResources[i].minPax;
                let maxPax = genericResources[i].maxPax;
                let activeStatus = genericResources[i].activeStatus;
                let type = genericResources[i].resourceType;
                let typeName = this.resourceTypes.find(value => value.code === genericResources[i].resourceType) ? this.resourceTypes.find(value => value.code === genericResources[i].resourceType).name : null;
                let currency = genericResources[i].currency;
                let paymentMethod = genericResources[i].paymentMethod;
                let phoneNumber = genericResources[i].phoneNumber;
                let city = genericResources[i].city;
                let cityName = genericResources[i].cityName;
                let preferredRoute = genericResources[i].preferredRoute;
                let preferredTransferMode = genericResources[i].preferredTransferMode;
                let preferredTransferModes = genericResources[i].preferredTransferModes;
                let languages = genericResources[i].languages;
                let yearsOfExperience = genericResources[i].yearsOfExperience;

                genericResourceMap.push({
                    'resourceId': id,
                    'name': name,
                    'supplierId': supplierId,
                    'supplierName': supplierName,
                    'supplierCode': supplierCode,
                    'supplier': supplierName + ' (' + supplierCode + ')',
                    'grade': grade,
                    'workingHours': workingHours,
                    'minPax': minPax,
                    'maxPax': maxPax,
                    'activeStatus': activeStatus,
                    'type': type,
                    'typeName': typeName,
                    'currency': currency,
                    'paymentMethod': paymentMethod,
                    'phoneNumber': phoneNumber,
                    'city': city,
                    'cityName': cityName,
                    'preferredRoute': preferredRoute,
                    'preferredTransferMode': preferredTransferMode,
                    'preferredTransferModes': preferredTransferModes,
                    'languages': languages,
                    'yearsOfExperience': yearsOfExperience,
                    'rawData': genericResources[i]
                });
            }
            this.genericResourceDataArray = genericResourceMap;
            this.contentCardDataList = this.getSearchContent();
        }
    }

    getSearchContent() {
        let contentCardList = this.summaryCardService.getContentCardsByTypeConfig(
            this.configs,
            this.genericResourceDataArray
        );

        this.spinnerService.hide();
        this.heading = this.resultCount + ' result' + (this.resultCount > 1 ? 's' : '') + ' found.';
        this.paginatorHasMore = this.resultCount !== contentCardList.length;

        this.setPageZero();
        return contentCardList;
    }

    public onResourceSelect($event: SummaryCardData) {
        this.selectedGenericResourceSummary = $event;
        this.getSelectedResourceCost();
        const id = $event.data['value']['resourceId'];
        if (id !== null) {
            this.spinnerService.show();
            this.genericResourceService.retrieveResourceById(id)
                .subscribe(
                    res => {
                        if (this.commonHelper.dataValidity(res)) {
                            this.spinnerService.hide();
                            this.detailedResource = ResponseUtil.getFirstData(res);
                            let selectedResource = {
                                'resourceId': this.detailedResource.resourceId,
                                'resourceType': this.detailedResource.resourceType
                            };

                            let parameters = this.queryParamService.getQueryParamsArray(selectedResource);
                            this.rootService.setDataToLocalStorage(TCO.AppData.RESOURCE, selectedResource, true, parameters);

                            // this.detailSupplier = res;
                            console.log(this.detailedResource);
                        } else if (res instanceof TcApiError) {
                            this.spinnerService.hide();
                        } else if (res instanceof TcHttpError) {
                            this.spinnerService.hide();
                        }
                    }
                );
        }
    }
    getSelectedResourceCost() {
        this.dataStore.set(DataKey.supplier, null, true);
        this.firstTimeSupplierResults = true;
        let id = this.selectedGenericResourceSummary.data['value']['supplierId'];
        this.supplierService.retrieveSupplierById(String(id));
        this.supplierResultObserver();
    }
    supplierResultObserver() {
        this.supplierSubscription = this.dataStore.get(DataKey.supplier).subscribe(
            result => {
                if (result != null && this.firstTimeSupplierResults) {
                    this.firstTimeSupplierResults = false;
                    this.resourceCost = [];
                    let supplier: Supplier = ResponseUtil.getFirstData(result);
                    if (supplier) {
                        for (let value of supplier.seasons) {
                           let genCosts: GenResourceCost[] = [];
                            for (const cost of value.genResourceCosts) {
                                if(cost.resourceType === this.selectedGenericResourceSummary.data['value']['type']){
                                    genCosts.push(cost);
                                }
                            }
                            value.genResourceCosts = genCosts;
                            if(value.genResourceCosts && value.genResourceCosts.length > 0){
                                this.resourceCost.push(value);
                            }
                        }
                    }
                }
            }, error => {
            }
        );
    }

    public onEditResource($event: Resource) {
        this.userJourneyManager.goForKey('GO_TO_EDIT_GENERIC_RESOURCE');
        $event.isEditTriggered = true;
        this.dataStore.set(DataKey.selectedGenericResourceForEdit, $event, true);
    }

    public onCalendarClick(event: SummaryCardData) {
        this.navConfig = this.configLoader.configurations.get(TCO.CONF.CONF_PAGE_SECTION_CONFIG)['availability_calendar_side_nav']['resource_level'];
        setTimeout(() => {
            let fabActions = [];

            /*let navItemsArray = this.configLoader.configurations.get(TCO.CONF.CONF_NAVIGATION);
             this.configLoader.configurations.get(TCO.CONF.CONF_FOCUS_VIEW).AVAILABILITY_CAL_FOCUS_VIEW.fabAction
             .forEach(val => {
             for (let key in navItemsArray) {
             if (val === navItemsArray[key]['id']) {
             fabActions.push(navItemsArray[key]);
             }
             }
             });*/
            let dataObject = new ModalData(
                LEVEL.SUCCESS,
                'Availability Calendar',
                null,
                null,
                'season-setup-focus-view',
                ResAvailabilityCalendarFocusViewComponent,
                {
                    'resourceSummary': event.data.value,
                    'navConfig': this.navConfig
                },
                {'label': 'close'},
                '',
                '',
                fabActions,
                '',
                (event && event.data.value) ? (event.data.value.name) + ' | Supplier: ' + (event.data.value.supplierName) : ''
            );

            this.focusViewCloseSubject = this.dataStore.get(DataKey.availabilityCalendarFocusViewClose).subscribe(c => {
                if (c) {
                }
            });
            this.focusViewService.confirm(dataObject).subscribe(res => {
                if (res) {
                    console.log(res);
                }
            });
        }, 0);
    }

    public onCardScroll($event: number) {
        setTimeout(() => {
            this.autoScrollService.scrollToID(
                'resourceDetailExp_' + $event, 'main-scroll-container');
        }, 0);
    }

    public onEditAsNewResource($event: Resource) {
        this.clearIdsInResource($event);
        this.userJourneyManager.goForKey('GO_TO_CREATE_GENERIC');
        this.dataStore.set(DataKey.selectedGenericResourceForEditAsNew, $event, true);
    }

    /**
     * IMPORTANT : clear all the existing ids that needs to be saved as new entities
     *
     * @param resource
     */
    public clearIdsInResource(resource: Resource) {
        resource.resourceId = 0;
        if (resource.contactDetails) {
            resource.contactDetails.forEach(value => value.contactId = 0);
        }
        if (resource.documents) {
            resource.documents.forEach(value => value.id = 0);
        }
        if (resource.remarks) {
            resource.remarks.forEach(value => value.remarkId = 0);
        }
        if (resource.taxSchemes) {
            resource.taxSchemes.forEach(value => value.taxSchemeId = 0);
        }
        if (resource.paymentInfo) {
            resource.paymentInfo.paymentId = 0;
        }
        if (resource.supplierAttributes) {
            resource.supplierAttributes.forEach(value => value.id = 0);
        }
        if (resource.genericResource) {
            resource.genericResource.genericResId = 0;

            if (resource.genericResource.languages) {
                resource.genericResource.languages.forEach(value => {
                    value.tblId = 0;
                    value.genericResourceId = 0;
                });
            }
        }
        if (resource.vehicle) {
            resource.vehicle.vehicleId = 0;

            if (resource.vehicle.facilities) {
                resource.vehicle.facilities.forEach(value => {
                    value.tblFacilityId = 0;
                    value.vehicleId = 0;
                });
            }
            if (resource.vehicle.resServiceInfo) {
                resource.vehicle.resServiceInfo.serviceInfoId = 0;
            }
            if (resource.vehicle.resInsuranceInfo) {
                resource.vehicle.resInsuranceInfo.insuranceInfoId = 0;
            }
        }
    }

    public ngOnDestroy(): void {
        if (this.genericSearchResultSubscription) {
            this.genericSearchResultSubscription.unsubscribe();
        }

        if (this.genericSearchResultFromServiceSubscription) {
            this.genericSearchResultFromServiceSubscription.unsubscribe();
        }

        if (this.genericSearchResultFromServiceOnMoreSubscription) {
            this.genericSearchResultFromServiceOnMoreSubscription.unsubscribe();
        }

        if (this.genericSearchCriteriaSubscription) {
            this.genericSearchCriteriaSubscription.unsubscribe();
        }

        if (this.supplierSubscription) {
            this.supplierSubscription.unsubscribe();
        }

        if (this.focusViewCloseSubject) {
            this.focusViewCloseSubject.unsubscribe();
        }
    }
}
